import { HeroAccordion } from '@components/HeroAccordion'
import { HeroIcon } from '@components/HeroIcon'
import { ICourse, ICourseModulesNodes } from '@dtos/Course'
import { IEnrollmentData } from '@contexts/EnrollmentContext'
import { useScrollBar } from '@hooks/useScrollbar'
import { useContentsModalStore } from '@stores/useContentsModalStore'
import { useGetModuleCourseContents } from '@services/api/graphql/moduleCourseContents'
import { COURSE_CONTENTS_PAGE_SIZE } from '@utils/submodulesPagination'
import {
  isContentAvailable,
  releaseContentDateText,
} from '@utils/contentAvailable'
import { useLinearProgressGate } from '@hooks/useLinearProgressGate'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { InView } from '@components/IntersectionObserver'
import { Spinner, Text, View, XStack, YStack } from 'tamagui'
import { LessonHorizontalCard } from './LessonHorizontalCard'
import { LoadMoreSentinel } from './LoadMoreSentinel'
import { useLocalSearchParams } from 'expo-router'

interface IAccordionModuleProps {
  module: ICourseModulesNodes
  enrollment?: IEnrollmentData | null
  course?: ICourse | null
  isLastModule?: boolean
  isSubmodule?: boolean
  isModuleOpen?: boolean
  onLoadMoreSubmodules?: (moduleId: number) => Promise<void>
  hasMoreSubmodules?: (module: ICourseModulesNodes) => boolean
}

export const AccordionModule = React.memo(function AccordionModule({
  module,
  enrollment,
  course,
  isLastModule,
  isSubmodule,
  isModuleOpen,
  onLoadMoreSubmodules,
  hasMoreSubmodules,
}: IAccordionModuleProps) {
  const [moduleState, setModuleState] = useState<ICourseModulesNodes>(module)
  const [canLoadMoreContents, setCanLoadMoreContents] = useState(true)
  const [isLoadingMoreContents, setIsLoadingMoreContents] = useState(false)
  const [lastLoadedCount, setLastLoadedCount] = useState(
    module.courseContents.nodes.length,
  )
  const { isOpen: isContentsModalOpen } = useContentsModalStore()
  const { isContentBlocked } = useLinearProgressGate()
  const { moduleId, courseId } = useLocalSearchParams()
  const { getModuleCourseContents } = useGetModuleCourseContents({
    id: `${courseId}`,
    moduleId: '',
    offset: 0,
    limit: COURSE_CONTENTS_PAGE_SIZE,
  })
  const { scrollTo } = useScrollBar()
  const modulePositionRef = useRef(0)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastModuleBorderBottomWidth = isLastModule
    ? '$border-width-hairline'
    : '$border-width-none'

  const isOpenAccordionDefault = isModuleOpen ? module.id.toString() : ''

  // Submódulos sem conteúdo carregado não são exibidos. O sentinel de "carregar
  // mais" precisa ficar no último submódulo VISÍVEL — antes ele era pendurado no
  // último da lista crua e desaparecia junto com ele.
  const visibleSubmodules = useMemo(
    () =>
      (module.courseModules || []).filter(
        (submodule) => submodule.courseContents.nodes.length > 0,
      ),
    [module.courseModules],
  )

  const canLoadMoreSubmodules = hasMoreSubmodules?.(module) ?? false

  const AccordionTitle = useMemo(() => {
    if (moduleState.courseContentsCount === 0) return null
    return `${moduleState.courseContentsCount} conteúdo${
      moduleState.courseContentsCount > 1 ? 's' : ''
    }`
  }, [moduleState.courseContentsCount])

  const handleScroll = useCallback(
    (y: number) => {
      if (y <= 0) return

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }

      scrollTimeoutRef.current = setTimeout(() => {
        scrollTo('contents-modal-scroll', y)
      }, 300)
    },
    [scrollTo],
  )

  useEffect(() => {
    if (
      isContentsModalOpen &&
      isOpenAccordionDefault &&
      modulePositionRef.current > 0
    ) {
      handleScroll(modulePositionRef.current)
    }
  }, [isContentsModalOpen, isOpenAccordionDefault, handleScroll])

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  const loadMoreContents = useCallback(async () => {
    if (!canLoadMoreContents || isLoadingMoreContents) return

    if (moduleState.courseContents.nodes.length > lastLoadedCount) return

    setIsLoadingMoreContents(true)

    try {
      // As próximas aulas são pedidas pelo id do PRÓPRIO módulo/submódulo.
      // Rebuscar o módulo pai com uma janela de submódulos (offset 0, limit 20)
      // quebrava qualquer submódulo além da 20ª posição: ele nunca vinha na
      // resposta, o find falhava e a paginação era desligada para sempre na
      // primeira página de aulas.
      const { data } = await getModuleCourseContents({
        variables: {
          moduleId: `${moduleState.id}`,
          offset: moduleState.courseContents.nodes.length,
        },
      })

      const newModuleLessons =
        data?.course.courseModules?.nodes?.[0]?.courseContents?.nodes

      if (newModuleLessons && newModuleLessons.length > 0) {
        setCanLoadMoreContents(true)
        setModuleState((prevState) => {
          const allContents = [
            ...prevState.courseContents.nodes,
            ...newModuleLessons,
          ]
          const uniqueContents = allContents.filter(
            (content, index, self) =>
              index === self.findIndex((c) => c.id === content.id),
          )
          return {
            ...prevState,
            courseContents: {
              nodes: uniqueContents,
            },
          }
        })
        setLastLoadedCount(
          moduleState.courseContents.nodes.length + newModuleLessons.length,
        )
      } else {
        setCanLoadMoreContents(false)
      }
    } finally {
      setIsLoadingMoreContents(false)
    }
  }, [
    canLoadMoreContents,
    getModuleCourseContents,
    isLoadingMoreContents,
    moduleState.id,
    moduleState.courseContents.nodes.length,
    lastLoadedCount,
  ])

  const isLessonCompleted = useCallback(
    (lessonId: number) => {
      return enrollment?.progressCard?.lessonsProgresses?.find(
        (lesson) => lesson.lessonId === lessonId,
      )
    },
    [enrollment?.progressCard?.lessonsProgresses],
  )

  return (
    <View
      borderTopWidth={1}
      borderColor="$border-default"
      borderBottomWidth={lastModuleBorderBottomWidth}
      onLayout={(event) => {
        const y = event.nativeEvent.layout.y
        if (y > 0) {
          modulePositionRef.current = y
        }
      }}
    >
      <HeroAccordion
        key={module.id}
        value={module.id.toString()}
        defaultValue={isOpenAccordionDefault}
        marginVertical="$spacing-inset-xs"
        accordionTitle={
          <YStack
            gap="$spacing-inset-nano"
            alignItems="flex-start"
            paddingHorizontal="$spacing-inset-sm"
            width="90%"
          >
            {isSubmodule && (
              <XStack
                borderBottomLeftRadius="$border-radius-lg"
                borderTopRightRadius="$border-radius-lg"
                bg="$bg-surface-raised"
                alignItems="center"
                paddingHorizontal="$spacing-inset-nano"
                paddingVertical="$spacing-inset-quarck"
                gap={10}
                alignSelf="flex-start"
              >
                <HeroIcon
                  icon={['fal', 'turn-down-right']}
                  color="$icon-secondary"
                  size={16}
                />
                <Text
                  color="$text-secondary"
                  ff="$mulishSemiBold"
                  fontWeight="600"
                  fontSize="$font-size-xxxs"
                >
                  Submódulo
                </Text>
              </XStack>
            )}
            {/* textAlign explícito: no web o gatilho do accordion é um
                <button> (tag do Collapsible.Trigger do tamagui) e herda
                text-align:center do user-agent, então títulos que quebram em
                mais de uma linha saíam centralizados. */}
            <Text
              fontSize="$font-size-xs"
              fontFamily="$mulishSemiBold"
              fontWeight="600"
              color="$text-primary"
              paddingRight="$spacing-inset-xs"
              textAlign="left"
              width="100%"
            >
              {moduleState.name}
            </Text>
            <Text
              fontSize="$font-size-xxs"
              fontFamily="$mulishRegular"
              color="$text-muted"
              fontWeight="400"
              textAlign="left"
              width="100%"
            >
              {AccordionTitle}
            </Text>
          </YStack>
        }
      >
        {moduleState.courseContents.nodes.map((content, index) => {
          const dataAvailable = {
            releaseAt: content.lesson.releaseAt,
            releaseAfter: content.lesson.releaseAfter,
            lockAt: content.lesson.lockAt,
            lockAfter: content.lesson.lockAfter,
            createdAt: enrollment?.activatedAt || '',
            courseClassRelease: enrollment?.courseClassRelease,
            courseClassReleaseOffset: content.lesson.courseClassReleaseOffset,
          }
          const shouldLoadMore =
            moduleState.courseContents.nodes.length - 3 <= index &&
            moduleState.courseContents.nodes.length <
              moduleState.courseContentsCount &&
            canLoadMoreContents &&
            !isLoadingMoreContents

          const isBlockedByLinearProgress = isContentBlocked(content.id)
          const checkIfContentIsAvailable =
            isContentAvailable(dataAvailable) && !isBlockedByLinearProgress

          const lessonCardProps = {
            dataLesson: content.lesson,
            isAvailable: Boolean(checkIfContentIsAvailable),
            availableLabel: releaseContentDateText(dataAvailable),
            hasNoContent: !content.lesson.media && !content.lesson.activity,
            courseContentsId: content.id,
            moduleId: moduleState.id,
            media: {
              ...content.lesson.media,
              coverImage: content.lesson.coverImage,
            },
            isCompleted: isLessonCompleted(content.lesson.id)?.completed,
            isBlockedByLinearProgress,
          }

          return (
            <React.Fragment key={content.id}>
              {shouldLoadMore ? (
                <LoadMoreSentinel threshold={0.5} onVisible={loadMoreContents}>
                  <LessonHorizontalCard key={content.id} {...lessonCardProps} />
                </LoadMoreSentinel>
              ) : (
                <LessonHorizontalCard key={content.id} {...lessonCardProps} />
              )}
            </React.Fragment>
          )
        })}
        {isLoadingMoreContents && (
          <View
            paddingVertical="$spacing-inset-xs"
            alignItems="center"
            justifyContent="center"
          >
            <Spinner size="small" color="$spinner-strong" />
          </View>
        )}
        {visibleSubmodules.map((submodule, index) => {
          const isSubmoduleOpen = submodule.id.toString() === moduleId
          const isLastSubmodule = index === visibleSubmodules.length - 1

          const submoduleElement = (
            <ContentsModules
              key={submodule.id}
              module={submodule}
              enrollment={enrollment}
              course={course}
              moduleId={submodule.id.toString()}
              isLastModule={false}
              isModuleOpen={isSubmoduleOpen}
              isSubmodule={true}
              onLoadMoreSubmodules={onLoadMoreSubmodules}
              hasMoreSubmodules={hasMoreSubmodules}
            />
          )

          return isLastSubmodule &&
            canLoadMoreSubmodules &&
            onLoadMoreSubmodules ? (
            <InView
              key={submodule.id}
              onChange={(inView) => inView && onLoadMoreSubmodules(module.id)}
              threshold={0.5}
            >
              {submoduleElement}
            </InView>
          ) : (
            submoduleElement
          )
        })}
      </HeroAccordion>
    </View>
  )
})

interface IContentsModulesProps {
  module: ICourseModulesNodes
  enrollment?: IEnrollmentData | null
  course?: ICourse | null
  moduleId: string | string[]
  onLoadMore?: () => void
  onLoadMoreSubmodules?: (moduleId: number) => Promise<void>
  hasMoreSubmodules?: (module: ICourseModulesNodes) => boolean
  isLastModule: boolean
  isModuleOpen: boolean
  isSubmodule: boolean
}

export const ContentsModules = React.memo(function ContentsModules({
  module,
  enrollment,
  course,
  isLastModule,
  isModuleOpen,
  onLoadMore,
  onLoadMoreSubmodules,
  hasMoreSubmodules,
  isSubmodule,
}: IContentsModulesProps) {
  const accordionModuleProps = {
    module,
    enrollment,
    course,
    isLastModule,
    isModuleOpen,
    isSubmodule,
    onLoadMoreSubmodules,
    hasMoreSubmodules,
  }
  return (
    <React.Fragment key={module.id}>
      {isLastModule ? (
        <InView
          onChange={(inView) => inView && onLoadMore?.()}
          threshold={0.5}
          style={{
            flex: 1,
          }}
        >
          <AccordionModule {...accordionModuleProps} />
        </InView>
      ) : (
        <AccordionModule {...accordionModuleProps} />
      )}
    </React.Fragment>
  )
})
